import { db, getMeta, META_KEYS } from "../../core/data/local/db";
import { uuidv7 } from "../../core/platform/id";
import type { SaleFinalizedEvent, SaleRefundedEvent, SaleVoidedEvent } from "../../core/domain/events/bus";
import { loadReplica } from "./data/replica";
import {
  MODULE_ID,
  earnFor,
  earnKey,
  reversalsFor,
  reverseKey,
  type EarnRecord,
  type LoyaltyRecord,
  type RedemptionRecord,
  type ReverseRecord,
} from "./data/records";
import { itemKind, refundReversal, stickersByLine, totalStickers, type ItemKind, type StickersByLine } from "./domain/earn";

/**
 * What the till writes when a carded sale is rung, voided or refunded
 * (specs/006-loyalty contracts/loyalty-sync.md; research R5).
 *
 * AFTER THE SALE, NEVER INSIDE IT. `commitSale()` emits `sale.finalized` only
 * once the sale is durable, so nothing here can roll one back. The envelope and
 * its record commit together in a transaction of their own.
 *
 * EVERY FAILURE STOPS HERE. A sale must never notice loyalty exists (FR-015),
 * so each handler catches, logs, and returns.
 */

/** What the cart carried for this module (a cart annotation). */
export interface LoyaltyAnnotation {
  cardCode: string;
  digest: string;
  /** Lines given away as rewards, and whether each was a drink or a pastry. */
  grants: Array<{ lineId: string; kind: "drink" | "pastry" }>;
}

export function isLoyaltyAnnotation(value: unknown): value is LoyaltyAnnotation {
  const candidate = value as Partial<LoyaltyAnnotation> | null;
  return (
    typeof candidate?.cardCode === "string" &&
    typeof candidate.digest === "string" &&
    Array.isArray(candidate.grants)
  );
}

async function thisTerminal(): Promise<string> {
  return (await getMeta<string>(META_KEYS.terminalId)) ?? "";
}

/** How each menu item counts: a manager's rule, else ounces-in-a-size (research R11). */
export async function kindResolver(menuItemIds: readonly string[]): Promise<(id: string) => ItemKind> {
  const [replica, variants] = await Promise.all([
    loadReplica(),
    db.variants.where("menuItemId").anyOf([...new Set(menuItemIds)]).toArray(),
  ]);
  const names = new Map<string, string[]>();
  for (const variant of variants) {
    names.set(variant.menuItemId, [...(names.get(variant.menuItemId) ?? []), variant.name]);
  }
  const rules = new Map(Object.entries(replica.rules));
  return (id) => itemKind(id, rules, names);
}

async function enqueue(
  type: "loyalty.earn" | "loyalty.reverse",
  payload: Record<string, unknown>,
  terminalId: string,
  keep: { key: string; shiftId: string | null; record: LoyaltyRecord },
): Promise<void> {
  await db.transaction("rw", db.outbox, db.moduleRecords, async () => {
    await db.outbox.put({
      opId: keep.record.envelopeOpId,
      type,
      payload,
      createdAt: keep.record.createdAt,
      terminalId,
      attempts: 0,
      state: "pending",
      lastError: null,
      nextAttemptAt: null,
    });
    await db.moduleRecords.put({
      opId: keep.key,
      moduleId: MODULE_ID,
      kind: keep.record.kind,
      shiftId: keep.shiftId,
      record: keep.record,
      createdAt: keep.record.createdAt,
    });
  });
}

/**
 * Record an earn: the sale's stickers and any rewards it redeemed. Shared by
 * the `sale.finalized` subscriber and "Add card" on a sale already rung.
 */
export async function recordEarn(input: {
  source: "sale" | "attach";
  saleId: string;
  shiftId: string | null;
  staffId: string;
  terminalId: string;
  cardCode: string;
  digest: string;
  lines: SaleFinalizedEvent["lines"];
  redemptions: RedemptionRecord[];
  createdAt: string;
}): Promise<EarnRecord | null> {
  const kindOf = await kindResolver(input.lines.map((line) => line.menuItemId));
  const byLine = stickersByLine(input.lines, kindOf);
  const stickers = totalStickers(byLine);

  // A card on a sale with no drinks and no reward leaves no trace at all.
  if (stickers === 0 && input.redemptions.length === 0) return null;

  const record: EarnRecord = {
    kind: "earn",
    envelopeOpId: uuidv7(),
    saleId: input.saleId,
    cardCode: input.cardCode,
    digest: input.digest,
    stickers,
    stickersByLine: byLine,
    redemptions: input.redemptions,
    createdAt: input.createdAt,
  };

  await enqueue(
    "loyalty.earn",
    {
      source: input.source,
      saleId: input.saleId,
      cardCode: input.cardCode,
      stickers,
      redemptions: input.redemptions,
      staffId: input.staffId,
      terminalId: input.terminalId,
      shiftId: input.shiftId,
      createdAt: input.createdAt,
    },
    input.terminalId,
    { key: earnKey(input.saleId), shiftId: input.shiftId, record },
  );
  return record;
}

export async function onSaleFinalized(event: SaleFinalizedEvent): Promise<void> {
  try {
    const annotation = event.annotations[MODULE_ID];
    if (!isLoyaltyAnnotation(annotation)) return;

    // The free item's full price is its grant: the discount row core wrote
    // for the line, naming this module.
    const granted = await db.discounts.where("saleId").equals(event.saleId).toArray();
    const amountFor = new Map(
      granted
        .filter((row) => row.grantedByModule === MODULE_ID && row.saleLineId)
        .map((row) => [row.saleLineId as string, row.amount as number]),
    );

    const redemptions: RedemptionRecord[] = annotation.grants
      .map((grant) => {
        const line = event.lines.find((candidate) => candidate.lineId === grant.lineId);
        if (!line || line.grantedByModule !== MODULE_ID) return null;
        return {
          id: uuidv7(),
          saleLineId: line.lineId,
          menuItemId: line.menuItemId,
          kind: grant.kind,
          fullPrice: amountFor.get(line.lineId) ?? 0,
        };
      })
      .filter((item): item is RedemptionRecord => item !== null);

    await recordEarn({
      source: "sale",
      saleId: event.saleId,
      shiftId: event.shiftId,
      staffId: event.staffId,
      terminalId: event.terminalId,
      cardCode: annotation.cardCode,
      digest: annotation.digest,
      lines: event.lines,
      redemptions,
      createdAt: event.createdAt,
    });
  } catch (error) {
    console.error(`[loyalty] could not record stickers for sale ${event.saleId}`, error);
  }
}

function sumByLine(records: readonly ReverseRecord[]): StickersByLine {
  const total: StickersByLine = {};
  for (const record of records) {
    for (const [lineId, n] of Object.entries(record.stickersByLine)) total[lineId] = (total[lineId] ?? 0) + n;
  }
  return total;
}

async function reverse(
  cause: "void" | "refund",
  causeId: string,
  staffId: string,
  createdAt: string,
  earn: EarnRecord,
  taken: StickersByLine,
  redemptionReversals: ReverseRecord["redemptionReversals"],
): Promise<void> {
  const stickers = -totalStickers(taken);
  if (stickers === 0 && redemptionReversals.length === 0) return;

  const terminalId = await thisTerminal();
  const sale = await db.sales.get(earn.saleId);
  const record: ReverseRecord = {
    kind: "reverse",
    envelopeOpId: uuidv7(),
    saleId: earn.saleId,
    cardCode: earn.cardCode,
    digest: earn.digest,
    stickers,
    stickersByLine: taken,
    redemptionReversals,
    createdAt,
  };

  await enqueue(
    "loyalty.reverse",
    {
      cause,
      causeId,
      saleId: earn.saleId,
      cardCode: earn.cardCode,
      stickers,
      redemptionReversals,
      staffId,
      terminalId,
      createdAt,
    },
    terminalId,
    { key: reverseKey(record.envelopeOpId), shiftId: sale?.shiftId ?? null, record },
  );
}

/** Rewards this sale redeemed that nothing has given back yet. */
function unreturned(earn: EarnRecord, previous: readonly ReverseRecord[]): RedemptionRecord[] {
  const returned = new Set(previous.flatMap((record) => record.redemptionReversals.map((r) => r.reversesId)));
  return earn.redemptions.filter((redemption) => !returned.has(redemption.id));
}

export async function onSaleVoided(event: SaleVoidedEvent): Promise<void> {
  try {
    const earn = await earnFor(event.saleId);
    if (!earn) return;
    const previous = await reversalsFor(event.saleId);
    const already = sumByLine(previous);

    // Everything the sale earned and has not already given back.
    const taken: StickersByLine = {};
    for (const [lineId, n] of Object.entries(earn.stickersByLine)) {
      const left = n - (already[lineId] ?? 0);
      if (left > 0) taken[lineId] = left;
    }
    const rewards = unreturned(earn, previous).map((r) => ({ ...r, id: uuidv7(), reversesId: r.id }));

    await reverse("void", event.voidId, event.staffId, event.createdAt, earn, taken, rewards);
  } catch (error) {
    console.error(`[loyalty] could not reverse stickers for voided sale ${event.saleId}`, error);
  }
}

export async function onSaleRefunded(event: SaleRefundedEvent): Promise<void> {
  try {
    const earn = await earnFor(event.saleId);
    if (!earn) return;
    const previous = await reversalsFor(event.saleId);

    const taken = refundReversal(earn.stickersByLine, sumByLine(previous), event.lines);
    // Refunding a free line gives its reward back (FR-023); it refunded no money.
    const refundedLines = new Set(event.lines.filter((line) => line.quantity > 0).map((line) => line.saleLineId));
    const rewards = unreturned(earn, previous)
      .filter((redemption) => refundedLines.has(redemption.saleLineId))
      .map((r) => ({ ...r, id: uuidv7(), reversesId: r.id }));

    await reverse("refund", event.refundId, event.staffId, event.createdAt, earn, taken, rewards);
  } catch (error) {
    console.error(`[loyalty] could not reverse stickers for refunded sale ${event.saleId}`, error);
  }
}
